import React from 'react';
import ModernHeader from '../components/common/ModernHeader';
import SEO from '../components/common/SEO';
import {
  APP_STORE_URL,
  PLAY_STORE_URL,
  APP_STORE_BADGE,
  GOOGLE_PLAY_BADGE,
} from '../constants/appStoreLinks';

const DownloadPage: React.FC = () => {
  const playStoreUrl = PLAY_STORE_URL;
  const appStoreUrl = APP_STORE_URL;

  // Generate QR codes using a free API service
  const playStoreQR = `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(playStoreUrl)}`;
  const appStoreQR = `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(appStoreUrl)}`;

  // Official badges
  const googlePlayBadge = GOOGLE_PLAY_BADGE;
  const appStoreBadge = APP_STORE_BADGE;

  return (
    <>
      <SEO
        title="Download Nemlingo"
        description="Get Nemlingo on iOS and Android. Learn Danish and English vocabulary with daily flashcards, native pronunciation, and a lock-screen widget."
      />
      <ModernHeader transparent={false} />

      <main className="bg-gradient-to-b from-white to-gray-50 min-h-[calc(100vh-4rem)]">
        <section className="container mx-auto px-4 py-16 md:py-24">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-extrabold tracking-tight text-primary">
              Download Nemlingo
            </h1>
            <p className="mt-4 text-lg text-gray-600">
              Master vocabulary faster with daily flashcards, native pronunciation, and a lock-screen widget. Available on iOS and Android.
            </p>

            {/* Desktop: QR Codes */}
            <div className="hidden md:flex mt-12 gap-8 justify-center items-start">
              {/* App Store */}
              <div className="flex flex-col items-center space-y-4 p-6 bg-white rounded-2xl shadow-lg">
                <img
                  src={appStoreBadge}
                  alt="Download on the App Store"
                  className="h-[50px] w-[150px] object-contain"
                  loading="lazy"
                />
                <div className="p-4 bg-white border-4 border-gray-200 rounded-xl">
                  <img
                    src={appStoreQR}
                    alt="App Store QR Code"
                    className="w-[200px] h-[200px]"
                    loading="lazy"
                  />
                </div>
                <p className="text-sm text-gray-500">Scan to download on iOS</p>
              </div>

              {/* Google Play */}
              <div className="flex flex-col items-center space-y-4 p-6 bg-white rounded-2xl shadow-lg">
                <img
                  src={googlePlayBadge}
                  alt="Get it on Google Play"
                  className="h-[50px] w-[150px] object-contain"
                  loading="lazy"
                />
                <div className="p-4 bg-white border-4 border-gray-200 rounded-xl">
                  <img
                    src={playStoreQR}
                    alt="Google Play QR Code"
                    className="w-[200px] h-[200px]"
                    loading="lazy"
                  />
                </div>
                <p className="text-sm text-gray-500">Scan to download on Android</p>
              </div>
            </div>

            {/* Mobile: Direct Links */}
            <div className="md:hidden mt-10 flex flex-col items-center gap-4">
              <a
                href={appStoreUrl}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Download on the App Store"
                className="transition-transform hover:scale-[1.02] active:scale-[0.98] focus:outline-none focus:ring-2 focus:ring-primary-500 rounded-lg"
              >
                <img
                  src={appStoreBadge}
                  alt="Download on the App Store"
                  className="h-[60px] w-[180px] object-contain"
                  loading="lazy"
                />
              </a>

              <a
                href={playStoreUrl}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Get it on Google Play"
                className="transition-transform hover:scale-[1.02] active:scale-[0.98] focus:outline-none focus:ring-2 focus:ring-primary-500 rounded-lg"
              >
                <img
                  src={googlePlayBadge}
                  alt="Get it on Google Play"
                  className="h-[60px] w-[180px] object-contain"
                  loading="lazy"
                />
              </a>

              <p className="mt-4 text-sm text-gray-500">
                Tap to open in the App Store or Google Play
              </p>
            </div>
          </div>
        </section>
      </main>
    </>
  );
};

export default DownloadPage;